import { motion } from "framer-motion";
import { Zap, MessageCircle, Send, Instagram, Shield, Cpu, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { signInWithGoogle } from "@/lib/auth";

const floatingElements = [
  { delay: 0, x: "10%", y: "20%", size: 60, opacity: 0.06 },
  { delay: 1, x: "80%", y: "15%", size: 80, opacity: 0.04 },
  { delay: 2, x: "70%", y: "70%", size: 50, opacity: 0.05 },
  { delay: 0.5, x: "20%", y: "75%", size: 70, opacity: 0.04 },
  { delay: 1.5, x: "50%", y: "10%", size: 40, opacity: 0.06 },
];

const features = [
  { icon: Shield, text: "Seguro & escalavel" },
  { icon: Cpu, text: "Automatizado com IA" },
  { icon: BarChart3, text: "Analytics em tempo real" },
];

export function LoginPage() {
  const handleGoogleLogin = async () => {
    try {
      await signInWithGoogle();
    } catch {
      // Toast handled by auth layer
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(230,30%,8%)] via-[hsl(250,40%,12%)] to-[hsl(270,35%,10%)] animate-gradient" />

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      {/* Floating decorative elements */}
      {floatingElements.map((el, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-primary"
          style={{
            left: el.x,
            top: el.y,
            width: el.size,
            height: el.size,
            opacity: el.opacity,
            filter: "blur(40px)",
          }}
          animate={{
            y: [0, -20, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 6,
            delay: el.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Main card */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative z-10 mx-4 w-full max-w-md"
      >
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 shadow-2xl backdrop-blur-xl sm:p-10">
          {/* Logo */}
          <div className="mb-8 flex flex-col items-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
              className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-indigo-400 shadow-xl shadow-primary/30"
            >
              <Zap className="h-8 w-8 text-white" fill="currentColor" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl font-bold tracking-tight text-foreground"
            >
              Dispara
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-2 text-center text-sm text-muted-foreground"
            >
              Automatize seus disparos com IA
            </motion.p>
          </div>

          {/* Platform badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-8 flex items-center justify-center gap-6"
          >
            <div className="flex items-center gap-1.5 text-emerald-400">
              <MessageCircle className="h-4 w-4" />
              <span className="text-xs font-medium">WhatsApp</span>
            </div>
            <div className="flex items-center gap-1.5 text-sky-400">
              <Send className="h-4 w-4" />
              <span className="text-xs font-medium">Telegram</span>
            </div>
            <div className="flex items-center gap-1.5 text-pink-400">
              <Instagram className="h-4 w-4" />
              <span className="text-xs font-medium">Instagram</span>
            </div>
          </motion.div>

          {/* Google Sign-in Button */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Button
              onClick={handleGoogleLogin}
              variant="outline"
              size="lg"
              className="w-full gap-3 border-white/10 bg-white/5 text-foreground hover:bg-white/10 h-12 text-sm font-medium"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Entrar com Google
            </Button>
          </motion.div>

          {/* Divider */}
          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Recursos
            </span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          {/* Features */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col gap-3"
          >
            {features.map((feature, i) => (
              <motion.div
                key={feature.text}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + i * 0.1 }}
                className="flex items-center gap-3 text-sm text-muted-foreground"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <feature.icon className="h-4 w-4 text-primary" />
                </div>
                <span>{feature.text}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-6 text-center text-xs text-muted-foreground/50"
        >
          Dispara v3.0 &mdash; Plataforma de disparos inteligentes
        </motion.p>
      </motion.div>
    </div>
  );
}
